import React, { ChangeEvent, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'

import { DescriptorType, ObjectDescriptor } from '../../../../models/Scorecard'
import {
  FormFieldFormTemplateMappingSource,
  FormTemplateMappingFormValues,
  FormTemplateMappingSource,
  FormTemplateMappingTarget,
  VariableFormTemplateMappingSourceId,
} from '../../../../models/FormTemplate'

import { Button } from '../../../../components/Button/Button'

import {
  countFormFieldSourcesInDescriptor,
  countMappedFormFieldSources,
  getFormFieldFormTemplateMappingSourcesFromDescriptorEntry,
  getOptionsForDescriptor,
  mappingExistsForSource,
  sortDescriptors,
} from '../utils'

import { FormFieldFormTemplateMappingSourceColumn, VariableFormTemplateMappingSourceColumn } from './FormTemplateMappingSourceColumn'
import { MultipleChoiceFormFieldFormTemplateMappingRows } from './MultipleChoiceFormFieldFormTemplateMappingRows'
import { SingleChoiceFormFieldFormTemplateMappingRows } from './SingleChoiceFormTemplateMappingRows'
import { FormTemplateMappingTargetColumn } from './FormTemplateMappingTargetColumn'

interface FormTemplateMappingFormProps {
  targets: FormTemplateMappingTarget[]
  mapping: Record<string, FormTemplateMappingSource>
  scorecardDescriptor: ObjectDescriptor
  onSubmit: (formData: FormTemplateMappingFormValues) => Promise<void>
  submitLoading: boolean
  handleDiscard?: () => void
}

export const FormTemplateMappingForm: React.FC<FormTemplateMappingFormProps> = ({
  targets,
  mapping,
  scorecardDescriptor,
  onSubmit,
  submitLoading,
  handleDiscard,
}) => {
  const form = useForm<FormTemplateMappingFormValues>({ defaultValues: { mapping } })

  return (
    <FormProvider {...form}>
      <div className='h-100 d-flex flex-column zocks-gap-2 form-template-update-container'>
        <div className='d-flex flex-column gap-1'>
          <h3>Edit document template mapping</h3>
          <span className='paragraph-medium'>Map form fields and variables to PDF fields.</span>
        </div>
        <FormTemplateMappingRows targets={targets} scorecardDescriptor={scorecardDescriptor} />
        <div className='d-flex gap-3 align-items-center justify-content-end zocks-px-1.75 py-3 rounded-3 position-fixed form-template-update-floating-toolbar z-2'>
          {handleDiscard ? (
            <Button className='px-3 py-2 button-medium' variant='secondaryGhostButton' onClick={handleDiscard}>
              Discard
            </Button>
          ) : null}
          <Button className='px-3 py-2 button-medium' variant='primary' onClick={form.handleSubmit(onSubmit)} loading={submitLoading}>
            Save
          </Button>
        </div>
      </div>
    </FormProvider>
  )
}

interface FormTemplateMappingRowsProps {
  targets: FormTemplateMappingTarget[]
  scorecardDescriptor: ObjectDescriptor
}

const FormTemplateMappingRows: React.FC<FormTemplateMappingRowsProps> = ({ targets, scorecardDescriptor }) => {
  const { watch } = useFormContext<FormTemplateMappingFormValues>()

  const mapping = watch('mapping')

  const [showUnmappedOnly, setShowUnmappedOnly] = useState<boolean>(false)

  const variableIds = Object.values(VariableFormTemplateMappingSourceId)

  const parentIds = new Set(targets.map((t) => t.parentId))
  const flatTargets = targets.filter((target) => !target.parentId && !parentIds.has(target.id))

  const sortedDescriptorEntries = useMemo(
    () => Object.entries(scorecardDescriptor.properties).sort(([, descriptorA], [, descriptorB]) => sortDescriptors(descriptorA, descriptorB)),
    [scorecardDescriptor],
  )

  const filteredDescriptorEntries = useMemo(
    () =>
      showUnmappedOnly
        ? sortedDescriptorEntries.filter(
            ([key, descriptor]) =>
              !getFormFieldFormTemplateMappingSourcesFromDescriptorEntry(key, descriptor).every((source) => mappingExistsForSource(source, mapping)),
          )
        : sortedDescriptorEntries,
    [mapping, showUnmappedOnly, sortedDescriptorEntries],
  )

  const totalFormFieldSourceCount = useMemo(() => countFormFieldSourcesInDescriptor(scorecardDescriptor), [scorecardDescriptor])

  const mappedFormFieldSourceCount = useMemo(() => countMappedFormFieldSources(mapping), [mapping])

  const formFieldMappingProgressPercent = useMemo(
    () => Math.floor((mappedFormFieldSourceCount / totalFormFieldSourceCount) * 100),
    [mappedFormFieldSourceCount, totalFormFieldSourceCount],
  )

  const handleShowUnmappedOnlyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowUnmappedOnly(e.target.checked)
  }

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex flex-column gap-3 position-sticky top-0 z-1 zocks-bg-color-white pt-3'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-2'>
            <Form.Check
              type='checkbox'
              id='show-unmapped-form-fields-checkbox'
              className='d-flex align-items-center justify-content-center form-template-update-checkbox'
              checked={showUnmappedOnly}
              onChange={handleShowUnmappedOnlyChange}
            />
            <label htmlFor='show-unmapped-form-fields-checkbox' className='paragraph-medium'>
              Show unmatched fields only
            </label>
          </div>
          <div className='d-flex flex-column position-relative'>
            <p className='paragraph-medium m-0'>
              <span className='bold'>
                {mappedFormFieldSourceCount} of {totalFormFieldSourceCount}
              </span>{' '}
              <span>Form fields matched</span>
            </p>
            <div
              className='position-absolute bottom-0 z-1 zocks-bg-color-primary form-template-update-progress rounded-2'
              style={{ width: `${formFieldMappingProgressPercent}%` }}
            />
            <div className='w-100 zocks-bg-color-gray-300 form-template-update-progress rounded-2' />
          </div>
        </div>
        <div className='d-flex align-items-center rounded-top-3 zocks-bg-color-gray-200'>
          <div className='w-50 d-flex align-items-center'>
            <span className='px-3 zocks-py-0.75 lead-extra-small'>Form field / Variable</span>
            <div className='form-template-update-spacer' />
          </div>
          <span className='w-50 px-3 zocks-py-0.75 lead-extra-small'>PDF field</span>
        </div>
      </div>
      <div className='d-flex flex-column border rounded-bottom-3 zocks-border-color-gray-200'>
        {filteredDescriptorEntries.map(([key, descriptor]) => {
          const options = getOptionsForDescriptor(descriptor)

          const source: FormFieldFormTemplateMappingSource = { type: 'form_field', path: `$.${key}` }

          const uiSchemaDescriptor = scorecardDescriptor.customWidgets?.[key]

          if (!options) {
            return (
              <div
                key={`form-field-form-template-mapping-row-${key}`}
                className='d-flex align-items-center border-top zocks-border-color-gray-200 form-template-mapping-row'
              >
                <FormFieldFormTemplateMappingSourceColumn descriptor={descriptor} uiSchemaDescriptor={uiSchemaDescriptor} />
                <FormTemplateMappingTargetColumn targets={flatTargets} source={source} />
              </div>
            )
          }

          if (descriptor.type === DescriptorType.STRING) {
            return (
              <SingleChoiceFormFieldFormTemplateMappingRows
                key={`single-choice-form-field-form-template-mapping-row-${key}`}
                descriptor={descriptor}
                descriptorOptions={options}
                uiSchemaDescriptor={uiSchemaDescriptor}
                targets={targets}
                source={source}
                showUnmappedOnly={showUnmappedOnly}
              />
            )
          }

          return (
            <MultipleChoiceFormFieldFormTemplateMappingRows
              key={`multiple-choice-form-field-form-template-mapping-row-${key}`}
              descriptor={descriptor}
              descriptorOptions={options}
              uiSchemaDescriptor={uiSchemaDescriptor}
              targets={flatTargets}
              source={source}
              showUnmappedOnly={showUnmappedOnly}
            />
          )
        })}
        {variableIds.map((variableId) => (
          <div
            key={`variable-form-template-mapping-row-${variableId}`}
            className='d-flex align-items-center border-top zocks-border-color-gray-200 form-template-mapping-row'
          >
            <VariableFormTemplateMappingSourceColumn variableId={variableId} />
            <FormTemplateMappingTargetColumn targets={flatTargets} source={{ type: 'variable', id: variableId }} />
          </div>
        ))}
      </div>
    </div>
  )
}
